// The rename-a-PAT write flow (#2828) — the state half of the tokens page's
// relabel step, split from the route exactly as `createTokenRevoke` is: a
// mutation with a dialog, a re-entrancy guard and a failure notice is state,
// not rendering. Per-surface, never a global store.
//
// It exists because naming a token only at the mint names nothing that already
// exists: every PAT an account holds today stores the same `dev_pat_` prefix and
// no name at all, so without a relabel the only way to identify one is to revoke
// it and mint a replacement — which breaks whatever was using it, and the reader
// cannot know what that was. That is the exact problem the name is for.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { type ApiToken, renameApiToken } from './tokens';

export type TokenRenameController = {
  /** The token being relabelled, or null. Feed to the <RenameTokenModal/>. */
  readonly target: () => ApiToken | null;
  /** True while the PATCH is in flight — disables the dialog's submit. */
  readonly pending: () => boolean;
  /** The API's reason for refusing, shown inline in the dialog. */
  readonly error: () => string | null;
  /** Open the dialog for a token, clearing any previous failure. */
  readonly ask: (token: ApiToken) => void;
  /** Dismiss without renaming. */
  readonly close: () => void;
  /** Rename the token under edit. A second call mid-flight is dropped. */
  readonly save: (name: string) => void;
};

/**
 * Rename state for one surface. `onRenamed` fires once the API has accepted —
 * the caller re-reads its roster rather than patching a row from the echo.
 *
 * A failure leaves the dialog OPEN with `error()` set: silently closing would
 * read as a successful rename, and the reader would go on identifying the token
 * by a label the server never stored.
 */
export function createTokenRename(input: {
  readonly token: string | null;
  readonly onRenamed: () => void;
}): TokenRenameController {
  const [target, setTarget] = createSignal<ApiToken | null>(null);
  const [pending, setPending] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const save = (name: string): void => {
    const row = target();
    if (row === null || pending()) return;
    setPending(true);
    setError(null);
    void renameApiToken(row.id, name, input.token)
      .then(() => {
        // Close only the dialog THIS call opened — nothing stops the reader
        // dismissing it and relabelling a different token mid-flight, and a
        // blind clear would dismiss that newer dialog out from under them.
        if (target()?.id === row.id) setTarget(null);
        input.onRenamed();
      })
      // Fenced to the dialog THIS call opened, exactly as the success path is:
      // nothing stops the reader dismissing it and relabelling a different token
      // mid-flight, and an unfenced set would show token A's failure inside
      // token B's dialog — a refusal attributed to a rename that never ran.
      .catch(() => {
        if (target()?.id === row.id) setError(t('account_tokens.rename_error'));
      })
      .finally(() => setPending(false));
  };

  return {
    target,
    pending,
    error,
    ask: (token) => {
      setError(null);
      setTarget(token);
    },
    close: () => setTarget(null),
    save,
  };
}
